<template>
  <div id="gjs" />
</template>

<script>
import 'grapesjs/dist/css/grapes.min.css';
import grapesjs from 'grapesjs';
import grapesJSMJML from 'grapesjs-mjml';

export default {
  props: {
    projectData: {
      type: Object,
      required: true,
    },
    onLoadAssets: {
      type: Function,
      required: true,
    },
    onStoreAssets: {
      type: Function,
      required: true,
    },
    onRemoveAsset: {
      type: Function,
      required: true,
    },
    onSave: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      editor: null,
    };
  },
  mounted() {
    if (typeof window.CKEDITOR !== 'undefined') {
      this.initializeEditor();
      return;
    }

    const ckeditorScript = document.createElement('script');
    ckeditorScript.setAttribute('src', 'https://cdn.ckeditor.com/4.21.0/full-all/ckeditor.js');

    const vm = this;
    ckeditorScript.onload = () => {
      vm.initializeEditor();
    };

    document.head.appendChild(ckeditorScript);
  },
  methods: {
    initializeEditor() {
      const vm = this;
      this.editor = grapesjs.init({
        container: '#gjs',
        fromElement: false,
        projectData: this.projectData,
        height: '100%',
        width: 'auto',
        plugins: [grapesJSMJML],
        pluginsOpts: {
          [grapesJSMJML]: {
            fonts: {
              Montserrat: 'https://fonts.googleapis.com/css2?family=Montserrat',
              'Open Sans': 'https://fonts.googleapis.com/css2?family=Open+Sans',
              Lato: 'https://fonts.googleapis.com/css2?family=Lato',
              Roboto: 'https://fonts.googleapis.com/css2?family=Roboto&display=swap',
            },
          },
        },
        assetManager: {
          custom: {
            open(props) {
              vm.$modal.show({
                component: () => import('./GrapesJsAssetManagerDialog.vue'),
                props: {
                  onLoadAssets: vm.onLoadAssets,
                  onSelectAsset: (asset) => {
                    const selected = vm.editor.getSelected();

                    if (selected && selected.is('mj-image')) {
                      selected.set('src', asset.url);
                    }
                  },
                  onRemoveAsset: (asset) =>
                    vm
                      .onRemoveAsset(asset.id)
                      .then(() => {
                        vm.editor.DomComponents.getWrapper()
                          .find(`[data-gjs-type=mj-image][src="${asset.url}"]`)
                          .forEach((component) => {
                            component.collection.remove(component);
                          });
                        props.am.remove(asset.url);

                        return Promise.resolve();
                      })
                      .catch((error) => Promise.reject(error)),
                  onStoreAssets: vm.onStoreAssets,
                  onClose: () => {
                    // IMPORTANT:
                    // When the external library is closed you have to comunicate
                    // this state back to the editor, otherwise GrapesJS will think
                    // the Asset Manager is still open.
                    props.close();
                  },
                },
                on: {},
                hideEvent: 'close',
              });
            },
            // eslint-disable-next-line no-unused-vars
            close(props) {
              // Close the external Asset Manager
            },
          },
        },
        colorPicker: {
          appendTo: 'parent',
          offset: { top: 26, left: -166 },
        },
      });

      this.editor.DomComponents.addType('mj-image', {
        model: {
          defaults: {
            void: false,
          },
        },
      });

      this.editor.DomComponents.addType('mj-image', {
        model: {
          defaults: {
            void: false,
          },
          toHTML() {
            let code = '';
            let model = this;
            let tag = model.get('tagName');
            let sTag = model.get('void');

            // Build the string of attributes
            let strAttr = '';
            let attr = this.getAttrToHTML();
            for (let prop in attr) {
              let val = attr[prop];
              strAttr +=
                typeof val !== 'undefined' && val !== '' ? ' ' + prop + '="' + val + '"' : '';
            }

            code += `<${tag}${strAttr}${sTag ? '/' : ''}>` + model.get('content');

            if (!sTag) code += `</${tag}>`;

            // Add the components after the closing tag.
            //
            // This will also fix an issue caused by changing void property value
            // where an imported template has an mj-image that is not enclosed by a closing tag,
            // the editor adds the mj-image closing tag right before its parent's closing tag.
            // Ultimately making all its sibling as its children.
            model.get('components').each((model) => {
              code += model.toHTML();
            });

            return code;
          },
        },
      });

      // add custom fonts options on editor's font list
      this.editor.on('load', () => {
        const styleManager = this.editor.StyleManager;
        const fontProperty = styleManager.getProperty('typography', 'font-family');

        const list = [];
        // empty list
        fontProperty.set('list', list);

        // custom list
        list.push(fontProperty.addOption({ value: 'Montserrat, sans-serif', name: 'Montserrat' }));
        list.push(fontProperty.addOption({ value: 'Open Sans, sans-serif', name: 'Open Sans' }));
        list.push(fontProperty.addOption({ value: 'Lato, sans-serif', name: 'Lato' }));
        list.push(fontProperty.addOption({ value: 'Roboto, sans-serif', name: 'Roboto' }));
        fontProperty.set('list', list);

        styleManager.render();
      });

      this.editor.on('update', () => {
        const mjml = this.editor.runCommand('mjml-code-to-html');
        this.$emit('input', {
          data: this.editor.getProjectData(),
          mjml,
        });
      });

      if (typeof this.onSave === 'function') {
        this.editor.Panels.addButton('options', [
          {
            id: 'save',
            className: 'fa fa-floppy-o icon-blank',
            // eslint-disable-next-line no-alert
            command(editor) {
              const mjml = editor.runCommand('mjml-get-code');
              vm.onSave({
                data: editor.getProjectData(),
                mjml,
              });
            },
            attributes: { title: 'Save Template' },
          },
        ]);
      }

      this.editor.setCustomRte({
        // parseContent: true,
        enable(el, rte) {
          // If already exists just focus
          if (rte) {
            this.focus(el, rte);
            return rte;
          }

          // eslint-disable-next-line no-param-reassign
          // el.contentEditable = true;

          // Seems like 'sharedspace' plugin doesn't work exactly as expected
          // so will help hiding other toolbars already created
          const rteToolbar = vm.editor.RichTextEditor.getToolbarEl();
          [].forEach.call(rteToolbar.children, (child) => {
            if (child.id === `cke_${el.id}`) child.remove();
            // eslint-disable-next-line no-param-reassign
            else child.style.display = 'none';
          });

          // CKEditor initialization
          // eslint-disable-next-line no-param-reassign
          rte = window.CKEDITOR.inline(el, {
            position: 'left',
            language: 'en',
            contentsCss: [
              'https://fonts.googleapis.com/css2?family=Montserrat',
              'https://fonts.googleapis.com/css2?family=Open+Sans',
              'https://fonts.googleapis.com/css2?family=Lato',
              'https://fonts.googleapis.com/css2?family=Roboto&display=swap',
            ],
            font_names: 'Arial;Helvetica;Times New Roman;Montserrat;Open Sans;Lato;Roboto',
            toolbarGroups: [
              { name: 'basicstyles', groups: ['basicstyles', 'cleanup'] },
              {
                name: 'paragraph',
                groups: ['list', 'indent', 'blocks', 'align', 'bidi'],
              },
              { name: 'links', groups: ['links'] },
              { name: 'colors', groups: ['colors'] },
              '/',
              { name: 'styles', groups: ['styles'] },
            ],
            removeButtons: 'NewPage',
            removePlugins: 'exportpdf',
            extraPlugins: ['sharedspace'],
            sharedSpaces: {
              top: rteToolbar,
            },
          });

          // eslint-disable-next-line no-param-reassign
          rte.getContent = rte.getData;

          // Make click event propogate
          rte.on('contentDom', () => {
            const editable = rte.editable();
            editable.attachListener(editable, 'click', () => {
              el.click();
            });
          });

          // The toolbar is not immediatly loaded so will be wrong positioned.
          // With this trick we trigger an event which updates the toolbar position
          rte.on('instanceReady', () => {
            const toolbar = rteToolbar.querySelector(`#cke_${rte.name}`);
            if (toolbar) {
              toolbar.style.display = 'block';
            }
            // eslint-disable-next-line no-undef
            vm.editor.Canvas.getWindow().dispatchEvent(new CustomEvent('scroll'));
          });

          const stopPropagation = (e) => {
            e.stopPropagation();
          };

          // Prevent blur when some of CKEditor's element is clicked
          rte.on('dialogShow', () => {
            const editorEls = grapesjs.$('.cke_dialog_background_cover, .cke_dialog');
            ['off', 'on'].forEach((m) => editorEls[m]('mousedown', stopPropagation));
          });

          this.focus(el, rte); // implemented later
          return rte;
        },
        disable(el, rte) {
          // eslint-disable-next-line no-param-reassign
          el.contentEditable = false;
          rte?.focusManager?.blur(true);
        },
        focus(el, rte) {
          // Do nothing if already focused
          if (rte?.focusManager?.hasFocus) {
            return;
          }
          // eslint-disable-next-line no-param-reassign
          el.contentEditable = true;
          rte.focus();
        },
        getContent(el, rte) {
          const htmlString = rte.getData();
          return htmlString;
        },
      });
    },
  },
};
</script>
